import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { RadioGroup, RadioGroupItem } from '../components/ui/radio-group';
import { Progress } from '../components/ui/progress';
import { ArrowRight, ArrowLeft, Building2, ClipboardCheck, User, Loader2, Users, Settings, Heart, UserCheck, Network } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// TeamCraft Framework Dimensions
const dimensions = [
  {
    id: 'team_resources',
    icon: Users,
    titleFR: 'Ressources de l\'équipe',
    titleEN: 'Team Resources',
    impactArea: 'Delivery Capacity',
    questions: [
      {
        id: '1A',
        fr: 'Nous disposons généralement du temps et des ressources nécessaires pour accomplir nos tâches dans les délais impartis.',
        en: 'We generally have the time and resources required to complete our work within the expected deadlines.'
      },
      {
        id: '1B',
        fr: 'Les compétences requises pour nos missions sont présentes au sein de l\'équipe, ou accessibles par des formations.',
        en: 'The skills required for our work are available within the team or accessible through training.'
      },
      {
        id: '1C',
        fr: 'La répartition du travail est équilibrée ; personne n\'est constamment en surcharge.',
        en: 'Workload is fairly distributed; no one is consistently overloaded.'
      }
    ]
  },
  {
    id: 'ways_of_working',
    icon: Settings,
    titleFR: 'Méthodes de travail & organisation',
    titleEN: 'Ways of Working & Organisation',
    impactArea: 'Execution Excellence',
    questions: [
      {
        id: '2A',
        fr: 'Nous comprenons la vision et la mission de l\'entreprise et savons comment notre travail y contribue.',
        en: 'We understand the company\'s vision and mission and how our work contributes to it.'
      },
      {
        id: '2B',
        fr: 'Les informations circulent librement et efficacement entre les membres de l\'équipe.',
        en: 'Information flows freely and efficiently among team members.'
      },
      {
        id: '2C',
        fr: 'Nous privilégions la réussite collective et célébrons les succès ensemble.',
        en: 'We prioritize collective success and celebrate achievements together.'
      }
    ]
  },
  {
    id: 'team_engagement',
    icon: Heart,
    titleFR: 'Engagement de l\'équipe',
    titleEN: 'Team Engagement',
    impactArea: 'Sustainable Performance',
    questions: [
      {
        id: '3A',
        fr: 'Les membres de l\'équipe se font confiance et partagent librement leurs idées.',
        en: 'Team members trust each other and openly share ideas and information.'
      },
      {
        id: '3B',
        fr: 'Nous avons confiance dans la direction et la stratégie de l\'organisation.',
        en: 'We trust the leadership and the organisation\'s strategy.'
      },
      {
        id: '3C',
        fr: 'Nous sommes motivés par notre mission et reconnus pour notre contribution.',
        en: 'We feel motivated by our mission and recognised for our contribution.'
      }
    ]
  },
  {
    id: 'manager_posture',
    icon: UserCheck,
    titleFR: 'Posture du manager',
    titleEN: 'Manager Posture',
    impactArea: 'Leadership Enablement',
    questions: [
      {
        id: '4A',
        fr: 'Notre manager donne une direction claire et fixe des objectifs alignés avec la stratégie.',
        en: 'Our manager provides clear direction and sets goals aligned with strategic priorities.'
      },
      {
        id: '4B',
        fr: 'Notre manager agit en coach et développe les compétences de l\'équipe.',
        en: 'Our manager acts as a coach and develops team capabilities.'
      },
      {
        id: '4C',
        fr: 'Notre manager facilite la collaboration et encourage les initiatives.',
        en: 'Our manager fosters collaboration and encourages initiative.'
      }
    ]
  },
  {
    id: 'ecosystem',
    icon: Network,
    titleFR: 'Écosystème & collaboration transverse',
    titleEN: 'Ecosystem & Cross-team Collaboration',
    impactArea: 'Organizational Alignment',
    questions: [
      {
        id: '5A',
        fr: 'En cas de désaccord, nous cherchons une solution commune avec les autres équipes.',
        en: 'In case of disagreement, we work with other teams to find a common solution.'
      },
      {
        id: '5B',
        fr: 'Nous collaborons activement avec les parties prenantes internes et externes.',
        en: 'We actively collaborate with internal and external stakeholders.'
      },
      {
        id: '5C',
        fr: 'Nous évaluons notre performance selon la valeur créée pour l\'entreprise et les clients.',
        en: 'We measure performance based on the value created for the business and its customers.'
      }
    ]
  }
];

const DiagnosisPage = () => {
  const { t, language } = useLanguage();
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(0); // 0 = org profile, 1-5 = dimensions, 6 = contact
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const [formData, setFormData] = useState({
    organization: {
      company_size: '',
      industry: '',
      growth_stage: '',
      team_size: ''
    },
    scores: {},
    contact: {
      name: '',
      company: '',
      email: '',
      role: '',
      phone: ''
    }
  });

  // Initialize scores for all questions
  React.useEffect(() => {
    const initialScores = {};
    dimensions.forEach(dim => {
      dim.questions.forEach(q => {
        initialScores[q.id] = 3;
      });
    });
    setFormData(prev => ({ ...prev, scores: initialScores }));
  }, []);

  const totalSteps = 7; // org + 5 dimensions + contact
  const progressPercentage = (currentStep / (totalSteps - 1)) * 100;

  const companySizes = [
    { value: '1-10', label: '1-10' },
    { value: '11-50', label: '11-50' },
    { value: '51-200', label: '51-200' },
    { value: '201-500', label: '201-500' },
    { value: '500+', label: '500+' }
  ];

  const industries = [
    { value: 'technology', label: 'Technology' },
    { value: 'finance', label: 'Finance & Banking' },
    { value: 'consulting', label: 'Consulting' },
    { value: 'manufacturing', label: 'Manufacturing' },
    { value: 'healthcare', label: 'Healthcare' },
    { value: 'retail', label: 'Retail' },
    { value: 'other', label: 'Other' }
  ];

  const growthStages = [
    { value: 'startup', label: 'Startup' },
    { value: 'scale-up', label: 'Scale-up' },
    { value: 'mature', label: 'Mature' },
    { value: 'enterprise', label: 'Enterprise' }
  ];

  const teamSizes = [
    { value: '1-5', label: '1-5' },
    { value: '6-15', label: '6-15' },
    { value: '16-30', label: '16-30' },
    { value: '31-50', label: '31-50' },
    { value: '50+', label: '50+' }
  ];

  const handleOrganizationChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      organization: { ...prev.organization, [field]: value }
    }));
  };

  const handleScoreChange = (questionId, value) => {
    setFormData(prev => ({
      ...prev,
      scores: { ...prev.scores, [questionId]: parseInt(value) }
    }));
  };

  const handleContactChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      contact: { ...prev.contact, [field]: value }
    }));
  };

  const isStep0Valid = () => {
    const { company_size, industry, growth_stage, team_size } = formData.organization;
    return company_size && industry && growth_stage && team_size;
  };

  const isContactValid = () => {
    const { name, company, email, role } = formData.contact;
    return name && company && email && role && email.includes('@');
  };

  const handleNext = () => {
    if (currentStep < totalSteps - 1) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handleBack = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleSubmit = async () => {
    setIsSubmitting(true);
    setError(null);

    try {
      // Calculate dimension scores
      const dimensionScores = {};
      dimensions.forEach(dim => {
        const questionScores = dim.questions.map(q => formData.scores[q.id] || 3);
        dimensionScores[dim.id] = questionScores.reduce((a, b) => a + b, 0) / questionScores.length;
      });

      const response = await axios.post(`${API}/diagnosis/submit`, {
        organization: formData.organization,
        scores: formData.scores,
        dimension_scores: dimensionScores,
        contact: formData.contact,
        language
      });

      navigate(`/results/${response.data.id}`, { state: { results: response.data } });
    } catch (err) {
      console.error('Submission error:', err);
      setError(err.response?.data?.detail || 'An error occurred. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const currentDimension = currentStep > 0 && currentStep < 6 ? dimensions[currentStep - 1] : null;

  return (
    <div className="pt-20 min-h-screen bg-white" data-testid="diagnosis-page">
      {/* Header */}
      <section className="py-10 bg-[#1A1A1A] text-white">
        <div className="container-custom">
          <p className="text-[#C5A059] uppercase tracking-widest text-sm font-medium mb-3">
            TeamCraft Diagnostic
          </p>
          <h1 className="text-3xl md:text-4xl font-bold mb-2">
            {language === 'fr' ? 'Diagnostic de Performance d\'Équipe' : 'Team Performance Diagnosis'}
          </h1>
          <Progress value={progressPercentage} className="h-1 bg-white/20 mt-6" />
        </div>
      </section>

      {/* Progress Steps */}
      <div className="bg-gray-50 border-b border-gray-200">
        <div className="container-custom py-4">
          <div className="flex items-center justify-between overflow-x-auto">
            {/* Step 0: Organization */}
            <div className="flex items-center gap-2 flex-shrink-0">
              <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                currentStep === 0 ? 'bg-[#C5A059] text-white' : currentStep > 0 ? 'bg-[#1A1A1A] text-white' : 'bg-gray-200 text-gray-500'
              }`}>
                <Building2 className="w-4 h-4" />
              </div>
              <span className="hidden md:block text-xs font-medium text-gray-600">Profile</span>
            </div>

            {/* Dimension Steps */}
            {dimensions.map((dim, idx) => (
              <React.Fragment key={dim.id}>
                <div className="w-8 h-0.5 bg-gray-200 flex-shrink-0"></div>
                <div className="flex items-center gap-2 flex-shrink-0">
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                    currentStep === idx + 1 ? 'bg-[#C5A059] text-white' : currentStep > idx + 1 ? 'bg-[#1A1A1A] text-white' : 'bg-gray-200 text-gray-500'
                  }`}>
                    <dim.icon className="w-4 h-4" />
                  </div>
                  <span className="hidden lg:block text-xs font-medium text-gray-600 max-w-[80px] truncate">{dim.titleEN.split(' ')[0]}</span>
                </div>
              </React.Fragment>
            ))}

            {/* Contact Step */}
            <div className="w-8 h-0.5 bg-gray-200 flex-shrink-0"></div>
            <div className="flex items-center gap-2 flex-shrink-0">
              <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                currentStep === 6 ? 'bg-[#C5A059] text-white' : 'bg-gray-200 text-gray-500'
              }`}>
                <User className="w-4 h-4" />
              </div>
              <span className="hidden md:block text-xs font-medium text-gray-600">Contact</span>
            </div>
          </div>
        </div>
      </div>

      {/* Form Content */}
      <section className="py-10 md:py-12">
        <div className="container-custom max-w-4xl">
          
          {/* Step 0: Organization Profile */}
          {currentStep === 0 && (
            <div className="animate-fade-in" data-testid="step-0-content">
              <h2 className="text-2xl font-semibold text-[#1A1A1A] mb-6">
                {language === 'fr' ? 'Profil de l\'Organisation' : 'Organization Profile'}
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <div className="space-y-2">
                  <Label>{language === 'fr' ? 'Taille de l\'entreprise' : 'Company Size'}</Label>
                  <Select value={formData.organization.company_size} onValueChange={(v) => handleOrganizationChange('company_size', v)}>
                    <SelectTrigger data-testid="select-company-size">
                      <SelectValue placeholder="Select..." />
                    </SelectTrigger>
                    <SelectContent>
                      {companySizes.map(s => <SelectItem key={s.value} value={s.value}>{s.label} employees</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label>{language === 'fr' ? 'Secteur d\'activité' : 'Industry'}</Label>
                  <Select value={formData.organization.industry} onValueChange={(v) => handleOrganizationChange('industry', v)}>
                    <SelectTrigger data-testid="select-industry">
                      <SelectValue placeholder="Select..." />
                    </SelectTrigger>
                    <SelectContent>
                      {industries.map(i => <SelectItem key={i.value} value={i.value}>{i.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label>{language === 'fr' ? 'Phase de croissance' : 'Growth Stage'}</Label>
                  <Select value={formData.organization.growth_stage} onValueChange={(v) => handleOrganizationChange('growth_stage', v)}>
                    <SelectTrigger data-testid="select-growth-stage">
                      <SelectValue placeholder="Select..." />
                    </SelectTrigger>
                    <SelectContent>
                      {growthStages.map(g => <SelectItem key={g.value} value={g.value}>{g.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label>{language === 'fr' ? 'Taille de l\'équipe' : 'Team Size'}</Label>
                  <Select value={formData.organization.team_size} onValueChange={(v) => handleOrganizationChange('team_size', v)}>
                    <SelectTrigger data-testid="select-team-size">
                      <SelectValue placeholder="Select..." />
                    </SelectTrigger>
                    <SelectContent>
                      {teamSizes.map(t => <SelectItem key={t.value} value={t.value}>{t.label} people</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>
          )}

          {/* Dimension Steps (1-5) */}
          {currentDimension && (
            <div className="animate-fade-in" data-testid={`step-${currentStep}-content`}>
              {/* Dimension Header */}
              <div className="flex items-center gap-4 mb-8 pb-6 border-b border-gray-200">
                <div className="w-14 h-14 bg-[#1A1A1A] rounded-lg flex items-center justify-center">
                  <currentDimension.icon className="w-7 h-7 text-[#C5A059]" />
                </div>
                <div>
                  <h2 className="text-xl font-semibold text-[#1A1A1A]">{currentDimension.titleEN}</h2>
                  <p className="text-gray-500">{currentDimension.titleFR}</p>
                  <span className="text-xs text-[#C5A059] font-medium mt-1 inline-block">→ {currentDimension.impactArea}</span>
                </div>
              </div>

              {/* Questions */}
              <div className="space-y-8">
                {currentDimension.questions.map((question, idx) => (
                  <div key={question.id} className="bg-gray-50 p-6 rounded-lg">
                    <div className="flex items-start gap-3 mb-5">
                      <span className="flex-shrink-0 w-8 h-8 bg-[#1A1A1A] text-white rounded-full flex items-center justify-center text-sm font-bold">
                        {question.id}
                      </span>
                      <div className="space-y-2">
                        <p className="text-[#1A1A1A] font-medium leading-relaxed">{question.en}</p>
                        <p className="text-gray-500 text-sm leading-relaxed italic">{question.fr}</p>
                      </div>
                    </div>

                    <div className="ml-11">
                      <div className="flex items-center justify-between text-xs text-gray-500 mb-3">
                        <span>{language === 'fr' ? 'Pas du tout d\'accord' : 'Strongly Disagree'}</span>
                        <span>{language === 'fr' ? 'Tout à fait d\'accord' : 'Strongly Agree'}</span>
                      </div>
                      <RadioGroup
                        value={(formData.scores[question.id] || 3).toString()}
                        onValueChange={(v) => handleScoreChange(question.id, v)}
                        className="flex justify-between"
                        data-testid={`score-${question.id}`}
                      >
                        {[1, 2, 3, 4, 5].map((score) => (
                          <div key={score} className="flex flex-col items-center gap-2">
                            <RadioGroupItem
                              value={score.toString()}
                              id={`${question.id}-${score}`}
                              className="w-10 h-10 border-2 data-[state=checked]:border-[#C5A059] data-[state=checked]:bg-[#C5A059]"
                            />
                            <Label htmlFor={`${question.id}-${score}`} className="text-sm font-mono font-medium">
                              {score}
                            </Label>
                          </div>
                        ))}
                      </RadioGroup>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Step 6: Contact Information */}
          {currentStep === 6 && (
            <div className="animate-fade-in" data-testid="step-6-content">
              <h2 className="text-2xl font-semibold text-[#1A1A1A] mb-6">
                {language === 'fr' ? 'Vos Informations' : 'Your Information'}
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <div className="space-y-2">
                  <Label>{language === 'fr' ? 'Nom complet' : 'Full Name'} *</Label>
                  <Input
                    value={formData.contact.name}
                    onChange={(e) => handleContactChange('name', e.target.value)}
                    placeholder="John Smith"
                    data-testid="input-name"
                  />
                </div>

                <div className="space-y-2">
                  <Label>{language === 'fr' ? 'Entreprise' : 'Company'} *</Label>
                  <Input
                    value={formData.contact.company}
                    onChange={(e) => handleContactChange('company', e.target.value)}
                    placeholder="Acme Corp"
                    data-testid="input-company"
                  />
                </div>

                <div className="space-y-2">
                  <Label>Email *</Label>
                  <Input
                    type="email"
                    value={formData.contact.email}
                    onChange={(e) => handleContactChange('email', e.target.value)}
                    placeholder="john@company.com"
                    data-testid="input-email"
                  />
                </div>

                <div className="space-y-2">
                  <Label>{language === 'fr' ? 'Fonction' : 'Role'} *</Label>
                  <Input
                    value={formData.contact.role}
                    onChange={(e) => handleContactChange('role', e.target.value)}
                    placeholder="Head of HR"
                    data-testid="input-role"
                  />
                </div>

                <div className="space-y-2 md:col-span-2">
                  <Label>{language === 'fr' ? 'Téléphone (optionnel)' : 'Phone (Optional)'}</Label>
                  <Input
                    value={formData.contact.phone}
                    onChange={(e) => handleContactChange('phone', e.target.value)}
                    placeholder="+352 621 123 456"
                    data-testid="input-phone"
                  />
                </div>
              </div>

              {error && (
                <div className="mt-6 p-4 bg-red-50 border border-red-200 text-red-700 rounded-lg" data-testid="error-message">
                  {error}
                </div>
              )}
            </div>
          )}

          {/* Navigation Buttons */}
          <div className="flex justify-between mt-10 pt-6 border-t border-gray-200">
            {currentStep > 0 ? (
              <Button variant="outline" onClick={handleBack} className="flex items-center gap-2" data-testid="back-btn">
                <ArrowLeft className="w-4 h-4" />
                {language === 'fr' ? 'Retour' : 'Back'}
              </Button>
            ) : (
              <div></div>
            )}

            {currentStep < totalSteps - 1 ? (
              <Button
                onClick={handleNext}
                disabled={currentStep === 0 && !isStep0Valid()}
                className="bg-[#1A1A1A] hover:bg-[#333] text-white flex items-center gap-2"
                data-testid="next-btn"
              >
                {language === 'fr' ? 'Continuer' : 'Continue'}
                <ArrowRight className="w-4 h-4" />
              </Button>
            ) : (
              <Button
                onClick={handleSubmit}
                disabled={!isContactValid() || isSubmitting}
                className="bg-[#C5A059] hover:bg-[#D4AF61] text-[#1A1A1A] flex items-center gap-2"
                data-testid="submit-btn"
              >
                {isSubmitting ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    {language === 'fr' ? 'Analyse en cours...' : 'Processing...'}
                  </>
                ) : (
                  <>
                    {language === 'fr' ? 'Obtenir mes résultats' : 'Get My Results'}
                    <ArrowRight className="w-4 h-4" />
                  </>
                )}
              </Button>
            )}
          </div>
        </div>
      </section>
    </div>
  );
};

export default DiagnosisPage;
